import dayjs from "dayjs";

// API Service for Cooperative Dashboard
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get authentication token from localStorage
  getAuthToken() {
    return localStorage.getItem('accessToken');
  }

  // Get refresh token from localStorage
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // Set tokens in localStorage
  setTokens(accessToken, refreshToken) {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  // Clear tokens from localStorage
  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Default headers with authentication
  getHeaders(skipContentType = false, skipAuth = false) {
    const token = this.getAuthToken();
    return {
      ...(skipContentType ? {} : { 'Content-Type': 'application/json' }),
      ...(!skipAuth && token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Refresh token method
  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Token refresh failed' }));
      throw new Error(errorData.message || 'Token refresh failed');
    }

    const data = await response.json();
    console.log('Token refreshed:', data);
    
    // Handle both response structures
    const tokenData = data.data || data;
    
    // Update tokens in localStorage
    if (tokenData && tokenData.access_token) {
      localStorage.setItem('accessToken', tokenData.access_token);
    }
    if (tokenData && tokenData.refresh_token) {
      localStorage.setItem('refreshToken', tokenData.refresh_token);
    }

    return data;
  }

  // Check if current page is login or register
  isAuthPage() {
    const currentPath = window.location.pathname;
    return currentPath.includes('/login') || currentPath.includes('/register') || currentPath.includes('/auth');
  }

  // Generic fetch wrapper with token refresh handling
  async fetchAPI(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Check if we're dealing with FormData (don't set Content-Type for FormData)
    const isFormData = options.body instanceof FormData;
    
    // Check if this is a public endpoint (no auth required)
    const isPublicEndpoint = endpoint.startsWith('/auth/') || 
                            endpoint.startsWith('/public/') || 
                            endpoint.startsWith('/provinces') || 
                            endpoint.startsWith('/cities') || 
                            endpoint.startsWith('/districts') || 
                            endpoint.startsWith('/villages');
    
    const config = {
      headers: this.getHeaders(isFormData, isPublicEndpoint),
      ...options
    };

    // Skip token-required endpoints if no token exists and user is on auth pages
    const token = this.getAuthToken();
    const isProtectedEndpoint = !isPublicEndpoint;
    
    if (!token && isProtectedEndpoint && this.isAuthPage()) {
      throw new Error('Authentication required');
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        // Handle token expiration (401 Unauthorized)
        if (response.status === 401 && !isRetry && token) {
          // Only try to refresh if we're not on auth pages
          if (!this.isAuthPage()) {
            try {
              // Try to refresh the token
              await this.refreshToken();
              
              // Retry the original request with new token
              const newConfig = {
                ...config,
                headers: this.getHeaders() // Get updated headers with new token
              };
              
              const retryResponse = await fetch(url, newConfig);
              const retryData = await retryResponse.json();
              
              if (!retryResponse.ok) {
                throw new Error(retryData.message || `HTTP error! status: ${retryResponse.status}`);
              }
              
              return retryData;
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // Clear tokens and redirect to login
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user_profile');
              window.location.href = '/auth/login';
              throw new Error('Session expired. Please login again.');
            }
          }
        }
        
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth API
  async login(email, password) {
    return this.fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  async register(name, email, password) {
    return this.fetchAPI('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password })
    });
  }

  async getProfile() {
    return this.fetchAPI('/profile');
  }

  // Product API
  async getProducts(page = 1, limit = 10, cooperativeId = null) {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    const endpoint = cooperativeId 
      ? `/products/cooperative/${cooperativeId}?${params}`
      : `/products?${params}`;
    return this.fetchAPI(endpoint);
  }

  async getProduct(id) {
    return this.fetchAPI(`/products/${id}`);
  }

  async createProduct(productData) {
    return this.fetchAPI('/products', {
      method: 'POST',
      body: JSON.stringify(productData)
    });
  }

  async createProductWithImages(productData, imageFiles = []) {
    const formData = new FormData();
    
    // Add product data to form
    Object.keys(productData).forEach(key => {
      if (productData[key] !== null && productData[key] !== undefined && productData[key] !== '') {
        formData.append(key, productData[key]);
      }
    });
    
    // Add image files
    imageFiles.forEach((file, index) => {
      formData.append('images', file);
    });
    
    // Set primary image index (default to first image)
    if (imageFiles.length > 0) {
      formData.append('primary_image_index', '0');
    }

    return this.fetchAPI('/products/with-images', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`
        // Don't set Content-Type for FormData, let browser set it
      },
      body: formData
    });
  }

  async updateProduct(id, productData) {
    return this.fetchAPI(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
  }

  async toggleFeaturedProduct(id) {
    return this.fetchAPI(`/products/${id}/toggle-featured`, {
      method: 'PATCH'
    });
  }

  async deleteProduct(id) {
    return this.fetchAPI(`/products/${id}`, {
      method: 'DELETE'
    });
  }

  async getProductsOnSale(page = 1, limit = 10) {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    return this.fetchAPI(`/products/sale?${params}`);
  }

  async getFeaturedProducts() {
    return this.fetchAPI('/products/featured');
  }

  async getProductsOnSaleByCooperative(cooperativeId) {
    return this.fetchAPI(`/products/cooperative/${cooperativeId}/sale`);
  }

  async getProductsByCategory(categoryId, page = 1, limit = 10) {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    return this.fetchAPI(`/products/category/${categoryId}?${params}`);
  }

  async getProductStatsByCooperative(cooperativeId) {
    return this.fetchAPI(`/cooperatives/${cooperativeId}/products/stats`);
  }

  async getTopProductsByCooperative(cooperativeId, sortBy = 'price', limit = 5) {
    const params = new URLSearchParams({ sort_by: sortBy, limit: limit.toString() });
    return this.fetchAPI(`/cooperatives/${cooperativeId}/products/top?${params}`);
  }

  // Category API
  async getCategories(cooperativeId = null, options = {}) {
    const { active, withChildren, rootOnly, page, limit, paginate } = options;
    
    let endpoint;
    let params = new URLSearchParams();
    
    if (cooperativeId) {
      endpoint = `/categories/cooperative/${cooperativeId}`;
      if (active) params.append('active', 'true');
      if (withChildren) params.append('with_children', 'true');
      if (rootOnly) params.append('root_only', 'true');
    } else {
      endpoint = '/categories';
    }
    
    // Add pagination parameters
    if (paginate) {
      params.append('paginate', 'true');
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());
    }
    
    const queryString = params.toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    // Create a unique key for caching/debouncing
    const requestKey = `categories_${fullEndpoint}`;
    
    // Simple debouncing: prevent the same request within 100ms
    const now = Date.now();
    if (this.lastRequestTime && this.lastRequestKey === requestKey) {
      const timeDiff = now - this.lastRequestTime;
      if (timeDiff < 100) {
        return this.lastRequestPromise;
      }
    }
    
    this.lastRequestTime = now;
    this.lastRequestKey = requestKey;
    this.lastRequestPromise = this.fetchAPI(fullEndpoint);
    
    return this.lastRequestPromise;
  }

  async getActiveCategoriesByCooperative(cooperativeId) {
    return this.getCategories(cooperativeId, { active: true });
  }

  async getRootCategoriesByCooperative(cooperativeId) {
    return this.getCategories(cooperativeId, { rootOnly: true });
  }

  async getCategoriesWithChildren(cooperativeId) {
    return this.getCategories(cooperativeId, { rootOnly: true, withChildren: true });
  }

  async getCategory(id) {
    return this.fetchAPI(`/categories/${id}`);
  }

  async getCategoryBySlug(slug) {
    return this.fetchAPI(`/categories/slug/${slug}`);
  }

  async createCategory(categoryData) {
    // Handle FormData differently than JSON
    const isFormData = categoryData instanceof FormData;
    const options = {
      method: 'POST'
    };
    
    if (isFormData) {
      // Don't set Content-Type header for FormData, let browser set it with boundary
      options.body = categoryData;
      options.headers = { ...this.getHeaders() };
      delete options.headers['Content-Type'];
    } else {
      options.body = JSON.stringify(categoryData);
    }
    
    return this.fetchAPI('/categories', options);
  }

  async updateCategory(id, categoryData) {
    // Handle FormData differently than JSON
    const isFormData = categoryData instanceof FormData;
    const options = {
      method: 'PUT'
    };
    
    if (isFormData) {
      // Don't set Content-Type header for FormData, let browser set it with boundary
      options.body = categoryData;
      options.headers = { ...this.getHeaders() };
      delete options.headers['Content-Type'];
    } else {
      options.body = JSON.stringify(categoryData);
    }
    
    return this.fetchAPI(`/categories/${id}`, options);
  }

  async deleteCategory(id) {
    return this.fetchAPI(`/categories/${id}`, {
      method: 'DELETE'
    });
  }

  async updateCategorySortOrder(id, sortOrder) {
    return this.fetchAPI(`/categories/${id}/sort-order`, {
      method: 'PUT',
      body: JSON.stringify({ sort_order: sortOrder })
    });
  }

  async generateCategorySlug(name) {
    return this.fetchAPI('/categories/generate-slug', {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  }

  // Product Images API
  async getProductImages(productId) {
    return this.fetchAPI(`/product-images/product/${productId}`);
  }

  async uploadProductImage(productId, imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('product_id', productId.toString());

    return this.fetchAPI('/product-images', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`
        // Don't set Content-Type for FormData, let browser set it
      },
      body: formData
    });
  }

  async setPrimaryImage(productId, imageId) {
    return this.fetchAPI(`/product-images/product/${productId}/primary/${imageId}`, {
      method: 'PUT'
    });
  }

  async deleteProductImage(imageId) {
    return this.fetchAPI(`/product-images/${imageId}`, {
      method: 'DELETE'
    });
  }

  // Cart API
  async getCart() {
    return this.fetchAPI('/cart');
  }

  async addToCart(productId, quantity = 1) {
    return this.fetchAPI('/cart/add', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity })
    });
  }

  async updateCartItem(cartItemId, quantity) {
    return this.fetchAPI(`/cart/update/${cartItemId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity })
    });
  }

  async removeFromCart(cartItemId) {
    return this.fetchAPI(`/cart/remove/${cartItemId}`, {
      method: 'DELETE'
    });
  }

  async clearCart() {
    return this.fetchAPI('/cart/clear', {
      method: 'DELETE'
    });
  }

  async getCartSummary() {
    return this.fetchAPI('/cart/summary');
  }

  // Orders API
  async getOrders(page = 1, limit = 10) {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    return this.fetchAPI(`/orders?${params}`);
  }

  async getOrdersByStatus(status, page = 1, limit = 10) {
    const params = new URLSearchParams({ 
      status,
      page: page.toString(), 
      limit: limit.toString() 
    });
    return this.fetchAPI(`/orders/status?${params}`);
  }

  async getOrder(orderId) {
    return this.fetchAPI(`/orders/${orderId}`);
  }

  async createOrder(orderData) {
    return this.fetchAPI('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }

  async cancelOrder(orderId) {
    return this.fetchAPI(`/orders/${orderId}/cancel`, {
      method: 'POST'
    });
  }

  async getOrderStatusHistory(orderId) {
    return this.fetchAPI(`/orders/${orderId}/status-history`);
  }

  // Menu Permission API
  async getUserMenus() {
    // Skip if no token or on auth pages
    if (!this.getAuthToken() || this.isAuthPage()) {
      console.log('🚫 getUserMenus: Skipping - no token or auth page');
      return { data: [] };
    }
    console.log('🔄 getUserMenus: Making API call...');
    const result = await this.fetchAPI('/menu/user-menus');
    console.log('✅ getUserMenus: API response:', result);
    return result;
  }

  async getMenuPermissions(userId = null) {
    // Skip if no token or on auth pages
    if (!this.getAuthToken() || this.isAuthPage()) {
      return { data: [] };
    }
    const params = userId ? `?user_id=${userId}` : '';
    return this.fetchAPI(`/menu/permissions${params}`);
  }

  async checkPermission(menuKey, action) {
    // Skip if no token or on auth pages - default to no permission
    if (!this.getAuthToken() || this.isAuthPage()) {
      return { data: { allowed: false } };
    }
    return this.fetchAPI(`/menu/check-permission/${menuKey}/${action}`);
  }

  // Role Management API
  async getAllRoles() {
    return this.fetchAPI('/roles');
  }

  async getRoleById(id) {
    return this.fetchAPI(`/roles/${id}`);
  }

  async createRole(roleData) {
    return this.fetchAPI('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  }

  async updateRole(id, roleData) {
    return this.fetchAPI(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    });
  }

  async deleteRole(id) {
    return this.fetchAPI(`/roles/${id}`, {
      method: 'DELETE'
    });
  }

  async getRoleWithPermissions(id) {
    return this.fetchAPI(`/roles/${id}/permissions`);
  }

  // Role Permission Management API
  async getRolePermissions(roleId) {
    return this.fetchAPI(`/role-permissions/${roleId}`);
  }

  async getRolePermissionsMatrix(roleId) {
    return this.fetchAPI(`/role-permissions/${roleId}/matrix`);
  }

  async updateRolePermissions(roleId, permissions) {
    return this.fetchAPI(`/role-permissions/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(permissions)
    });
  }

  async getAllMenuPermissions() {
    return this.fetchAPI('/menu-permissions');
  }

  async getMenuPermissionById(id) {
    return this.fetchAPI(`/menu-permissions/${id}`);
  }

  async createMenuPermission(menuPermissionData) {
    return this.fetchAPI('/menu-permissions', {
      method: 'POST',
      body: JSON.stringify(menuPermissionData)
    });
  }

  async updateMenuPermission(id, menuPermissionData) {
    return this.fetchAPI(`/menu-permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(menuPermissionData)
    });
  }

  async deleteMenuPermission(id) {
    return this.fetchAPI(`/menu-permissions/${id}`, {
      method: 'DELETE'
    });
  }

  async uploadCategoryImage(id, imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    const headers = { ...this.getHeaders() };
    delete headers['Content-Type']; // Let browser set Content-Type with boundary
    
    return this.fetchAPI(`/categories/${id}/image`, {
      method: 'POST',
      body: formData,
      headers
    });
  }

  async deleteCategoryImage(id) {
    return this.fetchAPI(`/categories/${id}/image`, {
      method: 'DELETE'
    });
  }

  async getProductByBarcode(barcode) {
    return this.fetchAPI(`/products/barcode/${barcode}`);
  }

  // News API
  async getNews(page = 1, limit = 10, search = '', isActive = null) {
    const params = new URLSearchParams({ 
      page: page.toString(), 
      limit: limit.toString(),
      ...(search && { search }),
      ...(isActive !== null && { is_active: isActive.toString() })
    });
    return this.fetchAPI(`/news?${params}`);
  }

  async getNewsById(id) {
    return this.fetchAPI(`/news/${id}`);
  }

  async getNewsBySlug(slug) {
    return this.fetchAPI(`/news/slug/${slug}`);
  }

  async getPublishedNews(page = 1, limit = 10, search = '') {
    const params = new URLSearchParams({ 
      page: page.toString(), 
      limit: limit.toString(),
      ...(search && { search })
    });
    return this.fetchAPI(`/news/published?${params}`, { requireAuth: false });
  }

  async getLatestNews(limit = 5) {
    const params = new URLSearchParams({ limit: limit.toString() });
    return this.fetchAPI(`/news/latest?${params}`, { requireAuth: false });
  }

  async createNews(newsData) {
    // Handle FormData differently than JSON
    const isFormData = newsData instanceof FormData;
    
    return this.fetchAPI('/news', {
      method: 'POST',
      ...(isFormData ? {
        body: newsData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      } : {
        body: JSON.stringify(newsData)
      })
    });
  }

  async updateNews(id, newsData) {
    // Handle FormData differently than JSON  
    const isFormData = newsData instanceof FormData;
    
    return this.fetchAPI(`/news/${id}`, {
      method: 'PUT',
      ...(isFormData ? {
        body: newsData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      } : {
        body: JSON.stringify(newsData)
      })
    });
  }

  async deleteNews(id) {
    return this.fetchAPI(`/news/${id}`, {
      method: 'DELETE'
    });
  }

  // Training API methods
  async getTraining(page = 1, limit = 10, search = '', isActive = null, level = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) params.append('search', search);
    if (isActive !== null) params.append('is_active', isActive.toString());
    if (level) params.append('level', level);

    return this.fetchAPI(`/trainings?${params.toString()}`);
  }

  async getTrainingById(id) {
    return this.fetchAPI(`/trainings/${id}`);
  }

  async createTraining(trainingData) {
    // Handle FormData differently than JSON
    const isFormData = trainingData instanceof FormData;
    
    return this.fetchAPI('/trainings', {
      method: 'POST',
      ...(isFormData ? {
        body: trainingData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      } : {
        body: JSON.stringify(trainingData)
      })
    });
  }

  async updateTraining(id, trainingData) {
    // Handle FormData differently than JSON  
    const isFormData = trainingData instanceof FormData;
    
    return this.fetchAPI(`/trainings/${id}`, {
      method: 'PUT',
      ...(isFormData ? {
        body: trainingData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      } : {
        body: JSON.stringify(trainingData)
      })
    });
  }

  async deleteTraining(id) {
    return this.fetchAPI(`/trainings/${id}`, {
      method: 'DELETE'
    });
  }

  // User Management methods
  async getAllUsers(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.role_id) queryParams.append('role_id', params.role_id);
    if (params.cooperative_id) queryParams.append('cooperative_id', params.cooperative_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    
    const url = `/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchAPI(url);
    return response;
  }

  async getUserById(id) {
    const response = await this.fetchAPI(`/users/${id}`);
    return response;
  }

  async createUser(userData) {
    const response = await this.fetchAPI('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    return response;
  }

  async updateUser(id, userData) {
    const response = await this.fetchAPI(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
    return response;
  }

  async deleteUser(id) {
    const response = await this.fetchAPI(`/users/${id}`, {
      method: 'DELETE'
    });
    return response;
  }

  async updateUserStatus(id, status) {
    const response = await this.fetchAPI(`/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    return response;
  }

  async getUsersByCooperative(cooperativeId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/users/cooperative/${cooperativeId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchAPI(url);
    return response;
  }

  async getUsersByRole(roleId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/users/role/${roleId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchAPI(url);
    return response;
  }

  async changeUserPassword(id, currentPassword, newPassword) {
    const response = await this.fetchAPI(`/users/${id}/change-password`, {
      method: 'PATCH',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });
    return response;
  }

  // Alias for easier use
  async changePassword(id, passwordData) {
    return this.changeUserPassword(id, passwordData.old_password, passwordData.new_password);
  }

  // Cooperative API (Public endpoint)
  async getCooperativesPublic() {
    return this.fetchAPI('/cooperatives');
  }

  // Generic HTTP methods for flexibility
  async get(endpoint, options = {}) {
    return this.fetchAPI(endpoint, {
      method: 'GET',
      ...options
    });
  }

  async post(endpoint, data = null, isFormData = false, options = {}) {
    return this.fetchAPI(endpoint, {
      method: 'POST',
      body: data ? (isFormData ? data : JSON.stringify(data)) : null,
      // Let fetchAPI handle headers properly
      ...options
    });
  }

  async put(endpoint, data = null, isFormData = false, options = {}) {
    return this.fetchAPI(endpoint, {
      method: 'PUT',
      body: data ? (isFormData ? data : JSON.stringify(data)) : null,
      // Let fetchAPI handle headers properly
      ...options
    });
  }

  async patch(endpoint, data = null, options = {}) {
    return this.fetchAPI(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
      ...options
    });
  }

  async delete(endpoint, options = {}) {
    return this.fetchAPI(endpoint, {
      method: 'DELETE',
      ...options
    });
  }
  
  // Bank Transfer API Methods
  async getBankTransfers(page = 1, limit = 10, search = '', isActive = null, cooperativeId = null) {
    if (cooperativeId) {
      // If cooperativeId is provided, use the cooperative-specific endpoint
      let queryParams = `page=${page}&limit=${limit}`;
    
      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }
      
      if (isActive !== null) {
        queryParams += `&is_active=${isActive}`;
      }
      
      return this.get(`/cooperatives/${cooperativeId}/bank-transfers?${queryParams}`);
    } else {
      // Otherwise use the general endpoint (for admin users)
      let queryParams = `page=${page}&limit=${limit}`;
      
      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }
      
      if (isActive !== null) {
        queryParams += `&is_active=${isActive}`;
      }
      
      return this.get(`/bank-transfers?${queryParams}`);
    }
  }
  
  async getBankTransferById(id) {
    return this.get(`/bank-transfers/${id}`);
  }
  
  async getBankTransfersByCooperative(cooperativeId) {
    return this.get(`/bank-transfers/cooperative/${cooperativeId}`);
  }
  
  async createBankTransfer(data) {
    return this.post('/bank-transfers', data);
  }
  
  async updateBankTransfer(id, data) {
    return this.put(`/bank-transfers/${id}`, data);
  }
  
  async deleteBankTransfer(id) {
    return this.delete(`/bank-transfers/${id}`);
  }
  
  async toggleBankTransferStatus(id, isActive) {
    return this.put(`/bank-transfers/${id}/status`, { 
      is_active: isActive
    });
  }
  
  // Bank Transfer Type API methods
  async getBankTransferTypes(page = 1, limit = 10, search = '', isActive = null) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (search) {
      params.append('search', search);
    }
    
    if (isActive !== null) {
      params.append('is_active', isActive.toString());
    }
    
    return this.get(`/bank-transfer-types?${params.toString()}`);
  }
  
  async getBankTransferTypeById(id) {
    return this.get(`/bank-transfer-types/${id}`);
  }
  
  async createBankTransferType(data) {
    // Use FormData for file uploads
    const formData = new FormData();
    
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        // Handle file upload separately
        if (key === 'icon' && data[key] instanceof File) {
          formData.append('icon', data[key]);
        } else if (key === 'is_active') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    
    return this.post('/bank-transfer-types', formData, true);
  }
  
  async updateBankTransferType(id, data) {
    // Use FormData for file uploads
    const formData = new FormData();
    
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        // Handle file upload separately
        if (key === 'icon' && data[key] instanceof File) {
          formData.append('icon', data[key]);
        } else if (key === 'is_active') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    
    return this.put(`/bank-transfer-types/${id}`, formData, true);
  }
  
  async deleteBankTransferType(id) {
    return this.delete(`/bank-transfer-types/${id}`);
  }
  
  async toggleBankTransferTypeStatus(id, isActive) {
    return this.put(`/bank-transfer-types/${id}/status`, {
      is_active: isActive
    });
  }

  async getProvinces() {
    return this.get('/provinces')
  }

  async getCities(provinceId) {
    return this.get(`/cities/province/${provinceId}`);
  }

  async getDistrict(cityId) {
    return this.get(`/districts/city/${cityId}`);
  }

  async getVillages(disctrictId) {
    return this.get(`/villages/district/${disctrictId}`);
  }

  async getPaymentMethodByCooperativeId(cooperativeId) {
    return this.get(`/payment-method-types/${cooperativeId}`);
  }
  // History Transaction
  async getSchedule(memberId, type, year) {
    return this.get(`/savings/deposit-schedules/report?member_id=${memberId}&type=${type}&year=${year}`)
  }

  /**
   * Get savings transaction detail
   * @param {number} transactionId - Transaction ID
   * @returns {Promise} Transaction detail
   */
  async getTransactionDetail(transactionId) {
    return this.get(`/savings/transactions/${transactionId}/detail`);
  }
  
  async approvalTransaction(id, action, debitAccountId = null, creditAccountId = null) {
    const payload = {
      action: action, 
      notes: '-'
    };
    
    // Add COA IDs if provided (for approve action)
    if (debitAccountId && creditAccountId) {
      payload.debit_account_id = parseInt(debitAccountId);
      payload.credit_account_id = parseInt(creditAccountId);
    }
    
    return this.post(`/savings/transactions/${id}/approve-or-cancel`, payload)
  }

  async approvalLoan(id, action, amount, term_months) {
    const payload = {
      action: action,
      adjusted_amount: amount, 
      adjusted_terms: term_months, 
      approval_notes: '', 
      rejection_reason: '',
    }
    return this.post(`/loans/${id}/approve`, payload)
  }

  async disburseLoan(id) {
    const payload = {
      disbursement_date: dayjs().format(),
      first_payment_date: dayjs().format(),
    }
    return this.post(`/loans/${id}/disburse`, payload)
  }

  // Loan Products Api Method
  async getLoanProducts(cooperativeId, page = 1, limit = 10, search = '') {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (search) {
      params.append('search', search);
    }
    
    return this.get(`/cooperatives/${cooperativeId}/loan-products?${params.toString()}`);
  }

  async getLoanProductsById(id) {
    return this.get(`/loan-products/${id}`);
  }
  
  async createLoanProducts(cooperativeId, data) {
    // Use FormData for file uploads
    const formData = new FormData();
    
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        // Handle file upload separately
        if (key === 'icon' && data[key] instanceof File) {
          formData.append('icon', data[key]);
        } else if (key === 'is_active') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    
    return this.post(`/cooperatives/${cooperativeId}/loan-products`, formData, true);
  }
  
  async updateLoanProducts(id, data) {
    // Use FormData for file uploads
    const formData = new FormData();
    
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        // Handle file upload separately
        if (key === 'icon' && data[key] instanceof File) {
          formData.append('icon', data[key]);
        } else if (key === 'is_active') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    
    return this.put(`/loan-products/${id}`, formData, true);
  }
  
  async deleteLoanProducts(id) {
    return this.delete(`/loan-products/${id}`);
  }

  async getLoanByCooperativeId(cooperativeId, page = 1, limit = 10, search = '') {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (search) {
      params.append('search', search);
    }
    
    return this.get(`/cooperatives/${cooperativeId}/loan?${params.toString()}`);
  }

  // ============ COOPERATIVE DASHBOARD API METHODS ============

  /**
   * Get comprehensive dashboard data for cooperative admin
   * @param {number} period - Number of days for historical data (7, 30, 90, 365)
   * @returns {Promise} Dashboard data including summary, charts, and recent activities
   */
  async getCooperativeDashboard(period = 30) {
    const params = new URLSearchParams();
    params.append('period', period.toString());
    
    return this.get(`/cooperative/dashboard?${params.toString()}`);
  }

  /**
   * Get cooperative summary statistics
   * @returns {Promise} Summary data (total members, savings, loans, orders, products)
   */
  async getCooperativeSummary() {
    return this.get('/cooperative/dashboard/summary');
  }

  /**
   * Get monthly statistics for charts
   * @param {number} months - Number of months to retrieve (default: 12)
   * @returns {Promise} Monthly data for savings, loans, and orders
   */
  async getCooperativeMonthlyStats(months = 12) {
    const params = new URLSearchParams();
    params.append('months', months.toString());
    
    return this.get(`/cooperative/dashboard/monthly-stats?${params.toString()}`);
  }

  /**
   * Get recent activities for cooperative
   * @param {number} limit - Number of activities to retrieve (default: 20)
   * @returns {Promise} Recent activities data
   */
  async getCooperativeRecentActivities(limit = 20) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    return this.get(`/cooperative/dashboard/recent-activities?${params.toString()}`);
  }

  /**
   * Get savings types breakdown for cooperative
   * @returns {Promise} Savings types with amounts and account counts
   */
  async getCooperativeSavingsTypes() {
    return this.get('/cooperative/dashboard/savings-types');
  }

  /**
   * Get loan status breakdown for cooperative
   * @returns {Promise} Loan status distribution data
   */
  async getCooperativeLoanStatus() {
    return this.get('/cooperative/dashboard/loan-status');
  }

  /**
   * Get order status breakdown for cooperative
   * @returns {Promise} Order status distribution data
   */
  async getCooperativeOrderStatus() {
    return this.get('/cooperative/dashboard/order-status');
  }

  /**
   * Get product statistics for cooperative
   * @returns {Promise} Product statistics and inventory data
   */
  async getCooperativeProductStats() {
    return this.get('/cooperative/dashboard/product-stats');
  }

  /**
   * Get member growth statistics
   * @param {number} period - Number of days for growth calculation
   * @returns {Promise} Member growth data
   */
  async getCooperativeMemberGrowth(period = 30) {
    const params = new URLSearchParams();
    params.append('period', period.toString());
    
    return this.get(`/cooperative/dashboard/member-growth?${params.toString()}`);
  }

  /**
   * Get financial health metrics
   * @returns {Promise} Financial health indicators
   */
  async getCooperativeFinancialHealth() {
    return this.get('/cooperative/dashboard/financial-health');
  }

  // ============================================================================
  // SUPER ADMIN DASHBOARD METHODS
  // ============================================================================

  /**
   * Get super admin dashboard data with cooperatives map and statistics
   * @param {number} period - Number of days for historical data (7, 30, 90, 365)
   * @returns {Promise} Super admin dashboard data including summary, cooperatives list, and analytics
   */
  async getSuperAdminDashboard(period = 30) {
    const params = new URLSearchParams();
    params.append('period', period.toString());
    
    return this.get(`/super-admin/dashboard?${params.toString()}`);
  }

  /**
   * Get all cooperatives with their locations for map display
   * @returns {Promise} List of cooperatives with coordinates and details
   */
  async getAllCooperatives() {
    return this.get('/super-admin/cooperatives');
  }

  /**
   * Get cooperatives grouped by province
   * @returns {Promise} Statistics of cooperatives per province
   */
  async getCooperativesByProvince() {
    return this.get('/super-admin/dashboard/cooperatives-by-province');
  }

  /**
   * Get top performing cooperatives
   * @param {number} limit - Number of top cooperatives to return (default: 10)
   * @returns {Promise} List of top performing cooperatives
   */
  async getTopCooperatives(limit = 10) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    return this.get(`/super-admin/dashboard/top-cooperatives?${params.toString()}`);
  }

  /**
   * Get system-wide recent activities
   * @param {number} limit - Number of activities to return (default: 20)
   * @returns {Promise} List of recent system activities
   */
  async getSystemActivities(limit = 20) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    return this.get(`/super-admin/dashboard/activities?${params.toString()}`);
  }

  /**
   * Get system-wide statistics summary
   * @returns {Promise} Overall system statistics
   */
  async getSystemSummary() {
    return this.get('/super-admin/dashboard/summary');
  }

  // Data Deletion API
  /**
   * Create a data deletion request
   * @param {Object} requestData - The data deletion request data
   * @returns {Promise} Response from the API
   */
  async createDataDeletionRequest(requestData) {
    return this.post('/data-deletion-request', requestData);
  }

  /**
   * Get all data deletion requests (Admin only)
   * @param {Object} params - Query parameters for pagination
   * @returns {Promise} Response from the API
   */
  async getDataDeletionRequests(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/admin/data-deletion-requests${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.get(url);
  }

  /**
   * Process a data deletion request (Admin only)
   * @param {number} id - The request ID
   * @param {Object} processData - The processing data (status, admin_notes)
   * @returns {Promise} Response from the API
   */
  async processDataDeletionRequest(id, processData) {
    return this.put(`/admin/data-deletion-requests/${id}/process`, processData);
  }

  // ============================================================================
  // REPORTING
  // ============================================================================

  async getCOA(cooperativeId, page = 1, limit = 10, search = '') {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (search) {
      params.append('search', search);
    }
    
    return this.get(`/api/accounting/chart-of-accounts?cooperative_id=${cooperativeId}&page=${page}&limit=999`);
  }

  async getChartOfAccountsByType(cooperativeId, accountType, accountGroup = '') {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());
    params.append('account_type', accountType);
    
    if (accountGroup) {
      params.append('account_group', accountGroup);
    }
    
    return this.get(`/api/accounting/chart-of-accounts/by-type?${params.toString()}`);
  }

  /**
   * Get simple chart of accounts list for filters
   * @param {number} cooperativeId - Cooperative ID
   * @param {string} accountType - Optional account type filter (ASSET, LIABILITY, EQUITY, INCOME, EXPENSE)
   * @param {number} level - Optional account level filter (1, 2, 3, etc.)
   * @param {string} search - Optional search term
   * @returns {Promise} Simplified list of chart of accounts
   */
  async getChartOfAccountsForFilter(cooperativeId, accountType = '', level = 0, search = '') {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());
    
    if (accountType) {
      params.append('account_type', accountType);
    }
    
    if (level > 0) {
      params.append('level', level.toString());
    }
    
    if (search) {
      params.append('search', search);
    }
    
    return this.get(`/api/accounting/chart-of-accounts/list?${params.toString()}`);
  }

  /**
   * Create manual journal entry
   * @param {Object} journalData - Journal entry data
   * @param {number} journalData.cooperative_id - Cooperative ID
   * @param {string} journalData.transaction_date - Transaction date (YYYY-MM-DD)
   * @param {string} journalData.description - Description
   * @param {Array} journalData.details - Array of journal entry details
   * @param {number} journalData.details[].account_id - Chart of Account ID
   * @param {string} journalData.details[].description - Detail description
   * @param {number} journalData.details[].debit_amount - Debit amount (0 if credit)
   * @param {number} journalData.details[].credit_amount - Credit amount (0 if debit)
   * @returns {Promise} Created journal entry
   */
  async createJournalEntry(journalData) {
    return this.post('/api/accounting/journal-entries', journalData);
  }

  /**
   * Get journal entries with pagination
   * @param {number} cooperativeId - Cooperative ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {string} search - Search term
   * @returns {Promise} Paginated journal entries
   */
  async getJournalEntries(cooperativeId, startDate = '', endDate = '', search = '', page = 1, limit = 10) {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (startDate) {
      params.append('start_date', startDate);
    }

    if (endDate) {
      params.append('end_date', endDate);
    }

    if (search) {
      params.append('search', search);
    }
    
    return this.get(`/api/accounting/journal-entries?${params.toString()}`);
  }

  async getCashBook(cooperativeId, startDate = '', endDate = '') {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());

    if (startDate) {
      params.append('start_date', startDate);
    }

    if (endDate) {
      params.append('end_date', endDate);
    }

    return this.get(`/api/reports/cash-book?${params.toString()}`);
  }
  
  async getLedger(cooperativeId, startDate = '', endDate = '', accountId = null) {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());

    if (startDate) {
      params.append('start_date', startDate);
    }

    if (endDate) {
      params.append('end_date', endDate);
    }

    if (accountId) {
      params.append('account_id', accountId);
    }

    return this.get(`/api/reports/general-ledger?${params.toString()}`);
  }

  async getTrialBalance(cooperativeId, date="") {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());

    if (date) {
      params.append('as_of_date', date);
    }

    return this.get(`/api/reports/trial-balance?${params.toString()}`);
  }

  async getTrialBalanceComplete(cooperativeId, date="") {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());

    if (date) {
      params.append('as_of_date', date);
    }

    return this.get(`/api/reports/trial-balance-complete?${params.toString()}`);
  }

  async getIncomeStatement(cooperativeId, startDate="", endDate="") {
    const params = new URLSearchParams();
    params.append('cooperative_id', cooperativeId.toString());

    if (startDate) {
      params.append('start_date', startDate);
    }

    if (endDate) {
      params.append('end_date', endDate);
    }

    return this.get(`/api/reports/income-statement?${params.toString()}`);
  }

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  /**
   * Get Orders and Products Dashboard Data
   * @param {number} period - Period in days (default: 30)
   * @returns {Promise} Response from the API
   */
  async getOrdersProductsDashboard(period = 30) {
    return this.get(`/dashboard/orders-products?period=${period}`);
  }

  /**
   * Get chart of account by ID
   * @param {number} accountId - Account ID
   * @returns {Promise} Account detail
   */
  async getChartOfAccountById(accountId) {
    return this.get(`/api/accounting/chart-of-accounts/${accountId}`);
  }

  /**
   * Create chart of account
   * @param {Object} accountData - Account data
   * @returns {Promise} Created account
   */
  async createChartOfAccount(accountData) {
    return this.post('/api/accounting/chart-of-accounts', accountData);
  }

  /**
   * Update chart of account
   * @param {number} accountId - Account ID
   * @param {Object} accountData - Updated account data
   * @returns {Promise} Update response
   */
  async updateChartOfAccount(accountId, accountData) {
    return this.put(`/api/accounting/chart-of-accounts/${accountId}`, accountData);
  }

  /**
   * Delete chart of account
   * @param {number} accountId - Account ID
   * @returns {Promise} Delete response
   */
  async deleteChartOfAccount(accountId) {
    return this.delete(`/api/accounting/chart-of-accounts/${accountId}`);
  }

  /**
   * Get journal entry by ID
   * @param {number} journalId - Journal entry ID
   * @returns {Promise} Journal entry detail
   */
  async getJournalEntryById(journalId) {
    return this.get(`/api/accounting/journal-entries/${journalId}`);
  }

  /**
   * Update journal entry
   * @param {number} journalId - Journal entry ID to update
   * @param {Object} journalData - Updated journal entry data
   * @returns {Promise} Update response
   */
  async updateJournalEntry(journalId, journalData) {
    return this.put(`/api/accounting/journal-entries/${journalId}`, journalData);
  }

  /**
   * Delete journal entry
   * @param {number} journalId - Journal entry ID to delete
   * @returns {Promise} Deletion response
   */
  async deleteJournalEntry(journalId) {
    return this.delete(`/api/accounting/journal-entries/${journalId}`);
  }

  // ============ COOPERATIVE SETTINGS API METHODS ============

  /**
   * Get cooperative settings
   * @returns {Promise} Response from the API
   */
  async getCooperativeSettings() {
    return this.get('/settings/cooperative');
  }

  /**
   * Update cooperative general information
   * @param {Object} data - General information data to update
   * @returns {Promise} Response from the API
   */
  async updateCooperativeGeneralInfo(data) {
    return this.put('/settings/cooperative/general-info', data);
  }

  /**
   * Get accounting settings
   * @returns {Promise} Response from the API
   */
  async getAccountingSettings() {
    return this.get('/settings/accounting');
  }

  /**
   * Update accounting settings
   * @param {Object} data - Accounting settings data to update
   * @returns {Promise} Response from the API
   */
  async updateAccountingSettings(data) {
    return this.put('/settings/accounting', data);
  }

  /**
   * Get membership settings
   * @returns {Promise} Response from the API
   */
  async getMembershipSettings() {
    return this.get('/settings/membership');
  }

  /**
   * Update membership settings
   * @param {Object} data - Membership settings data to update
   * @returns {Promise} Response from the API
   */
  async updateMembershipSettings(data) {
    return this.put('/api/settings/membership', data);
  }

  /**
   * Get savings settings
   * @returns {Promise} Response from the API
   */
  async getSavingsSettings() {
    return this.get('/settings/savings');
  }

  /**
   * Update savings settings
   * @param {Object} data - Savings settings data to update
   * @returns {Promise} Response from the API
   */
  async updateSavingsSettings(data) {
    return this.put('/settings/savings', data);
  }

  /**
   * Get all savings types
   * @returns {Promise} Response from the API
   */
  async getSavingsTypes() {
    return this.get('/savings-types');
  }

  /**
   * Get savings type by ID
   * @param {number} id - Savings type ID
   * @returns {Promise} Response from the API
   */
  async getSavingsTypeById(id) {
    return this.get(`/savings-types/${id}`);
  }

  /**
   * Alias for getSavingsTypeById
   * @param {number} id - Savings type ID
   * @returns {Promise} Response from the API
   */
  async getSavingsType(id) {
    return this.getSavingsTypeById(id);
  }

  /**
   * Create new savings type
   * @param {Object} data - Savings type data
   * @returns {Promise} Response from the API
   */
  async createSavingsType(data) {
    return this.post('/savings-types', data);
  }

  /**
   * Update savings type
   * @param {number} id - Savings type ID
   * @param {Object} data - Savings type data to update
   * @returns {Promise} Response from the API
   */
  async updateSavingsType(id, data) {
    return this.put(`/savings-types/${id}`, data);
  }

  /**
   * Delete savings type
   * @param {number} id - Savings type ID
   * @returns {Promise} Response from the API
   */
  async deleteSavingsType(id) {
    return this.delete(`/savings-types/${id}`);
  }

  // ============================================================================
  // Loan Settings API
  // ============================================================================

  /**
   * Get loan settings for the current cooperative
   * @returns {Promise} Response from the API
   */
  async getLoanSettings() {
    return this.get("/settings/loans");
  }

  /**
   * Update loan settings for the current cooperative
   * @param {Object} data - Loan settings data
   * @returns {Promise} Response from the API
   */
  async updateLoanSettings(data) {
    return this.put("/settings/loans", data);
  }

  // ============================================================================
  // Loan Products API (Settings CRUD)
  // ============================================================================

  /**
   * Get all loan products for the current cooperative
   * @returns {Promise} Response from the API
   */
  async getLoanProducts() {
    return this.get("/loan-product-types");
  }

  /**
   * Get loan product by ID
   * @param {number} id - Loan product ID
   * @returns {Promise} Response from the API
   */
  async getLoanProductById(id) {
    return this.get(`/loan-product-types/${id}`);
  }

  /**
   * Create a new loan product
   * @param {Object} data - Loan product data
   * @returns {Promise} Response from the API
   */
  async createLoanProduct(data) {
    return this.post("/loan-product-types", data);
  }

  /**
   * Update loan product
   * @param {number} id - Loan product ID
   * @param {Object} data - Updated loan product data
   * @returns {Promise} Response from the API
   */
  async updateLoanProduct(id, data) {
    return this.put(`/loan-product-types/${id}`, data);
  }

  /**
   * Delete loan product
   * @param {number} id - Loan product ID
   * @returns {Promise} Response from the API
   */
  async deleteLoanProduct(id) {
    return this.delete(`/loan-product-types/${id}`);
  }

  // ============================================================================
  // SHU Settings API
  // ============================================================================

  /**
   * Get SHU settings for the current cooperative
   * @returns {Promise} Response from the API
   */
  async getSHUSettings() {
    return this.get("/settings/shu");
  }

  /**
   * Update SHU settings for the current cooperative
   * @param {Object} data - SHU settings data
   * @returns {Promise} Response from the API
   */
  async updateSHUSettings(data) {
    return this.put("/settings/shu", data);
  }

  // ============================================================================
  // CLOSING PERIOD API
  // ============================================================================

  /**
   * Get all closing periods with filters
   * @param {Object} params - Query parameters (year, month, status, page, limit)
   * @returns {Promise} Response from the API
   */
  async getClosingPeriods(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/closing-periods${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get single closing period by ID
   * @param {number} id - Closing period ID
   * @returns {Promise} Response from the API
   */
  async getClosingPeriod(id) {
    return this.get(`/closing-periods/${id}`);
  }

  /**
   * Create new closing period
   * @param {Object} data - Closing period data (period_year, period_month, notes)
   * @returns {Promise} Response from the API
   */
  async createClosingPeriod(data) {
    return this.post("/closing-periods", data);
  }

  /**
   * Update closing period
   * @param {number} id - Closing period ID
   * @param {Object} data - Updated closing period data
   * @returns {Promise} Response from the API
   */
  async updateClosingPeriod(id, data) {
    return this.put(`/closing-periods/${id}`, data);
  }

  /**
   * Delete closing period (only draft periods)
   * @param {number} id - Closing period ID
   * @returns {Promise} Response from the API
   */
  async deleteClosingPeriod(id) {
    return this.delete(`/closing-periods/${id}`);
  }

  /**
   * Close a period (lock transactions and generate financial summary)
   * @param {number} id - Closing period ID
   * @param {Object} data - Close period data (closing_date, notes)
   * @returns {Promise} Response from the API
   */
  async closePeriod(id, data) {
    return this.post(`/closing-periods/${id}/close`, data);
  }

  /**
   * Reopen a closed period
   * @param {number} id - Closing period ID
   * @param {Object} data - Reopen period data (notes - required)
   * @returns {Promise} Response from the API
   */
  async reopenPeriod(id, data) {
    return this.post(`/closing-periods/${id}/reopen`, data);
  }

  /**
   * Get outlet settings
   * @returns {Promise} Response from the API
   */
  async getOutletSettings() {
    return this.get('/settings/outlet');
  }

  /**
   * Update outlet settings
   * @param {Object} data - Outlet settings data to update
   * @returns {Promise} Response from the API
   */
  async updateOutletSettings(data) {
    return this.put('/settings/outlet', data);
  }

  // ============================================================================

}

export default new ApiService();
